import { addDays, endOfToday, startOfToday } from 'date-fns';
import { shouldClearDateFiltersOnJobsVisit } from '~/helpers/jobCreationHints';

export type FilterStorage = {
  get: (key: string) => string | null | undefined;
  set: (key: string, value: string) => void;
};

export function defaultFilters(isMobile: boolean): string[][] {
  const today = startOfToday();
  const sevenDaysAgo = addDays(today, -7);

  if (isMobile) {
    // Removed default date filters for mobile
    return [];
  }

  const fourteenDaysFromToday = addDays(today, 14);

  return [
    ['scheduledDateAfter', sevenDaysAgo.toISOString()],
    ['scheduledDateBefore', fourteenDaysFromToday.toISOString()],
    ['completedDateAfter', sevenDaysAgo.toISOString()],
    ['completedDateBefore', endOfToday().toISOString()],
  ];
}

export function loadInitialFilters(
  storage: FilterStorage,
  view: string | undefined,
  isMobile: boolean,
  assigneeId: string | null
) {
  // Use safe default instead of early return
  if (!view) {
    return new URLSearchParams();
  }

  try {
    // Check if coming from onboarding - clear date filters if so
    // This check happens during initialization to ensure it runs before any effects
    // that might re-save the filters. The flag is consumed (removed) by the check.
    const shouldClearDateFilters = shouldClearDateFiltersOnJobsVisit();

    // Get existing filters
    const storedFilters = storage.get('form.filter.job-list');
    let filters = new URLSearchParams();

    // Handle potentially invalid stored filters
    try {
      filters = new URLSearchParams(storedFilters || []);
    } catch (e) {
      console.error('Invalid stored filters, resetting:', e);
      storage.set('form.filter.job-list', '');
      return new URLSearchParams(defaultFilters(isMobile));
    }

    // Clear date filters if coming from onboarding
    // This ensures newly created jobs are visible without date restrictions
    if (shouldClearDateFilters) {
      filters.delete('scheduledDateAfter');
      filters.delete('scheduledDateBefore');
      filters.delete('completedDateAfter');
      filters.delete('completedDateBefore');
      filters.delete('_scheduledDateCustomized');
      filters.delete('_completedDateCustomized');
      // Mark as customized to prevent re-applying defaults on remount
      // This ensures cleared filters stay cleared even if component remounts
      filters.set('_userCustomized', 'true');
      // Save immediately so other effects don't restore old values
      storage.set('form.filter.job-list', filters.toString());
    }

    // Mobile devices have different filter behavior
    if (isMobile) {
      // Check for customization flags
      const hasAssigneeCustomized = filters.has('_assigneeCustomized');

      // Only add assignee ID if it hasn't been customized
      if (assigneeId && !hasAssigneeCustomized) {
        filters.set('assignee', assigneeId);
      } else if (hasAssigneeCustomized && filters.get('assignee') === '') {
        // Ensure empty assignee stays empty when customized
        filters.set('assignee', '');
      }
    } else if (!shouldClearDateFilters && !filters.has('_userCustomized')) {
      // Only apply defaults if:
      // 1. NOT coming from onboarding (shouldClearDateFilters is false)
      // 2. User hasn't customized filters yet (_userCustomized flag is absent)
      // This prevents re-applying defaults after onboarding has cleared them
      const defaults = new URLSearchParams(defaultFilters(isMobile));
      defaults.forEach((value, key) => {
        if (!filters.has(key)) {
          filters.set(key, value);
        }
      });
    }

    // Note: We don't check for job hints here in loadInitialFilters
    // because this function runs during initialization before state is ready.
    // The checkAndAdjustFilters effect handles hint checking and filter expansion.

    return filters;
  } catch (error) {
    // If any error occurs during loading, return defaults
    console.error('Error loading filters, using defaults:', error);
    return new URLSearchParams(defaultFilters(isMobile));
  }
}
